import * as moment from 'moment';
import { Moment } from 'moment/moment';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { TripStatus } from './trip-status';
import { LoadHandover } from '../../load-handover/shared/load-handover.model';
import { TripPingLight } from './trip-ping-light.model';
import { TripDevicePath } from './trip-path.model';
import { LocalLocationType } from '../../local-loads/shared/local-location-type';
import { TripEvent } from './trip-event.model';
import { EtaReport } from './eta-report.model';
import { EtaEmitter } from './eta-emitter.enum';
import { TripDelayType } from './trip-delay-type';
import LatLngLiteral = google.maps.LatLngLiteral;
import { DateService } from '../../shared/date.service';

const NB_SCALE = 5;

export class Trip {

  private static readonly NB_MINUTES_BEFORE_EXPIRATION = 5;
  private static readonly NB_DAYS_BEFORE_OUTDATED     = 15;

  public tripId: string;
  public timezone: string;
  public customerId: string;
  public loadUuid: string;
  public loadId: string;
  public loadNumber: string;
  public originStopId: string;
  public destStopId: string;
  public deliveryNumbers: string[];
  public salesOrderNumbersByDeliveryNumbers: Map<string, string>;
  public deliveryNumbersInline: string;
  public firstDeliveryNumber: string;
  public vehicle: string;
  public status: TripStatus;
  public carrier: Carrier;
  public loadPlanningStart: Moment;
  public loadPlanningEnd: Moment;
  public checkInDateTime: Moment;
  public checkOutDateTime: Moment;
  public recordedAt: Moment;
  public startedAt: Moment;
  public completedAt: Moment;
  public trailerId: string;

  public originLocationRef: string;
  public originAddress: string;
  public originCity: string;
  public originPostalCode: string;
  public originRegionName: string;
  public originCountryCode: string;
  public originLocationType: LocalLocationType;

  public destLocationRef: string;
  public destAddress: string;
  public destCity: string;
  public destPostalCode: string;
  public destRegionName: string;
  public destCountryCode: string;
  public destLocationType: LocalLocationType;

  public originPosition: LatLngLiteral;
  public destPosition: LatLngLiteral;

  public latestPing: TripPingLight;
  public latestPingCreatedAt: Moment;

  public achievedPaths: TripDevicePath[] = [];
  public handoverAchievedPath: any[]      = [];
  public forecastPath: LatLngLiteral[]   = [];
  public achievedPathsCalculatedAt: Moment;
  public forecastPathCalculatedAt: Moment;
  public distanceToArrival: string;
  public estimatedEtaCalculatedAt: Moment;

  public driverEta: Moment;
  public driverEtaUpdatedAt: Moment;

  public automaticEta: Moment;
  public automaticEtaUpdatedAt: Moment;

  public loadHandovers: LoadHandover[] = [];

  public outdated = false;

  public timelineParamsInitialized = false;
  public events: TripEvent[] = [];
  public etas: EtaReport[] = [];

  static fromDto(t): Trip {
    const trip = new Trip();

    trip.tripId           = t.trip_id;
    trip.timezone         = t.timezone;
    trip.customerId       = t.customer_id;
    trip.loadUuid         = t.load_uuid;
    trip.loadId           = t.load_id;
    trip.loadNumber       = t.load_number;
    trip.originStopId     = t.origin_stop_id;
    trip.destStopId       = t.dest_stop_id;
    trip.deliveryNumbers  = t.delivery_numbers || [];
    trip.salesOrderNumbersByDeliveryNumbers = new Map(Object.entries(t.sales_order_numbers_by_delivery_numbers)) || new Map();
    trip.vehicle          = t.vehicle_id;
    trip.status           = t.status;
    trip.carrier          = Carrier.fromDto(t.carrier);
    trip.loadPlanningStart = t.load_planning_start ? moment(t.load_planning_start) : null;
    trip.loadPlanningEnd  = t.load_planning_end ? moment(t.load_planning_end) : null;
    trip.checkInDateTime  = t.check_in_date_time ? moment(t.check_in_date_time) : null;
    trip.checkOutDateTime = t.check_out_date_time ? moment(t.check_out_date_time) : null;
    trip.recordedAt       = t.recorded_at ? moment(t.recorded_at) : null;
    trip.startedAt        = t.started_at ? moment(t.started_at) : null;
    trip.completedAt      = t.completed_at ? moment(t.completed_at) : null;
    trip.trailerId        = t.trailer_id;

    trip.originLocationRef = t.origin_location_ref;
    trip.originAddress     = t.origin_address1;
    trip.originCity        = t.origin_city;
    trip.originPostalCode  = t.origin_postal_code;
    trip.originRegionName  = t.origin_region_name;
    trip.originCountryCode = t.origin_country_code;
    trip.originLocationType = t.origin_location_type;

    if (t.origin_latitude && t.origin_longitude) {
      trip.originPosition = { lat: t.origin_latitude, lng: t.origin_longitude };
    }

    trip.destLocationRef = t.dest_location_ref;
    trip.destAddress     = t.dest_address1;
    trip.destCity        = t.dest_city;
    trip.destPostalCode  = t.dest_postal_code;
    trip.destRegionName  = t.dest_region_name;
    trip.destCountryCode = t.dest_country_code;
    trip.destLocationType = t.dest_location_type;

    if (t.dest_latitude && t.dest_longitude) {
      trip.destPosition = { lat: t.dest_latitude, lng: t.dest_longitude };
    }

    trip.driverEta          = t.driver_eta ? moment(t.driver_eta) : null;
    trip.driverEtaUpdatedAt = t.driver_eta_updated_at ? moment(t.driver_eta_updated_at) : null;

    trip.automaticEta          = t.automatic_eta ? moment(t.automatic_eta) : null;
    trip.automaticEtaUpdatedAt = t.automatic_eta_updated_at ? moment(t.automatic_eta_updated_at) : null;

    trip.latestPing          = TripPingLight.fromDto(t.latest_ping);
    trip.latestPingCreatedAt = t.latest_ping_created_at ? moment(t.latest_ping_created_at) : null;

    trip.outdated = trip.isOutdated();

    trip.firstDeliveryNumber   = trip.deliveryNumbers[0];
    trip.deliveryNumbersInline = trip.deliveryNumbers.join(', ');

    return trip;
  }

  isPlanned(): boolean {
    return this.status === TripStatus.PLANNED;
  }

  isInProgress(): boolean {
    return this.status === TripStatus.IN_PROGRESS;
  }

  isNotTerminated(): boolean {
    return this.status !== TripStatus.COMPLETED && this.status !== TripStatus.EXPIRED && this.status !== TripStatus.CANCELLED;
  }

  isCompleted(): boolean {
    return this.status === TripStatus.COMPLETED;
  }

  isExpired(): boolean {
    return this.status === TripStatus.EXPIRED;
  }

  isOutdated(): boolean {
    if (this.isInProgress()) {
      return !this.recordedAt || moment().diff(this.recordedAt, 'days') > Trip.NB_DAYS_BEFORE_OUTDATED;
    } else {
      return !this.recordedAt || !this.completedAt || this.completedAt.diff(this.recordedAt, 'days') > Trip.NB_DAYS_BEFORE_OUTDATED
    }
  }

  isEstimatedETAUpToDate(): boolean {
    const now = moment();

    return this.estimatedEtaCalculatedAt &&
      this.estimatedEtaCalculatedAt.clone()
        .add(Trip.NB_MINUTES_BEFORE_EXPIRATION, 'minutes')
        .isAfter(now);
  }

  isForecastPathUpToDate(): boolean {
    const now = moment();
    return this.forecastPathCalculatedAt &&
      this.forecastPathCalculatedAt.clone()
        .add(Trip.NB_MINUTES_BEFORE_EXPIRATION, 'minutes')
        .isAfter(now);
  }

  isAchievedPathUpToDate(): boolean {
    const now = moment();
    return this.achievedPathsCalculatedAt &&
      this.achievedPathsCalculatedAt.clone()
        .add(Trip.NB_MINUTES_BEFORE_EXPIRATION, 'minutes')
        .isAfter(now);
  }

  eta(): Moment {
    if (this.driverEta) {
      return this.driverEta;
    }
    return this.automaticEta;
  }

  latestEta(): Moment {
    if (this.driverEta && this.automaticEta) {
      return this.driverEta.isBefore(this.automaticEta) ? this.automaticEta : this.driverEta;
    } else {
      return this.eta();
    }
  }

  etaUpdatedAt(): Moment {
    if (this.driverEta) {
      return this.driverEtaUpdatedAt;
    }

    return this.automaticEtaUpdatedAt;
  }

  latestEtaOrigin() {
    if (this.driverEta) {
      return this.latestEta().isSame(this.driverEta) ? EtaEmitter.DRIVER : EtaEmitter.CMBL_AUTOMATIC_ETA;
    } else if (this.automaticEta) {
      return EtaEmitter.CMBL_AUTOMATIC_ETA;
    }
  }

  latestEtaUpdatedAt(): Moment {
    if (this.driverEta) {
      if (this.automaticEta && this.driverEta.isBefore(this.automaticEta)) {
        return this.automaticEtaUpdatedAt;
      }
      return this.driverEtaUpdatedAt;
    }

    return this.automaticEtaUpdatedAt;
  }

  etaEmitter(): EtaEmitter {
    if (this.driverEta) {
      return EtaEmitter.DRIVER;
    }
    return EtaEmitter.CMBL_AUTOMATIC_ETA;
  }

  getTripStart(): Moment {
    if (this.isPlanned()) {
      return this.loadPlanningStart;
    }

    const eligibleDates = [
      ...this.events.map(event => event.referenceDate)
    ];
    if (this.startedAt) {
      eligibleDates.push(this.startedAt);
    }
    const firstPingCreatedAt     = this.achievedPaths[0]?.paths[0]?.firstPing?.createdAt;
    if (firstPingCreatedAt != null) {
      eligibleDates.push(firstPingCreatedAt);
    }
    if (this.latestPingCreatedAt) {
      eligibleDates.push(this.latestPingCreatedAt);
    }

    return moment.min(eligibleDates).clone();
  }

  getTripEnd(withoutRemainder = false): Moment {
    if (this.isCompleted() || this.isExpired()) {
      return this.completedAt;
    }

    if (this.isPlanned()) {
      return this.loadPlanningEnd;
    }

    const now = moment();
    const tripStart = this.getTripStart();
    const eligibleDates = [
      tripStart,
      ...this.events.map(event => event.referenceDate)
    ];
    if (this.latestPingCreatedAt) {
      eligibleDates.push(this.latestPingCreatedAt);
    }
    if (this.isNotTerminated()) {
      if (this.loadPlanningEnd) {
        eligibleDates.push(this.loadPlanningEnd);
      }
      eligibleDates.push(now);
    }
    if (this.latestEta()) {
      eligibleDates.push(this.latestEta());
    }

    let max = moment.max(eligibleDates).clone();

    if (max.isBefore(now) && !withoutRemainder) {
      const remainder = 60 - (now.clone().add(now.diff(tripStart) / NB_SCALE).minutes()) % 60;
      max = now.clone().add(now.diff(tripStart) / NB_SCALE).add(remainder, 'minutes');
    }
    return max;
  }

  getTripStatus(): TripDelayType {
    if (!this.loadPlanningEnd) {
      return TripDelayType.UNKNOWN;
    }

    if (
      (this.isNotTerminated() && this.loadPlanningEnd.isBefore(moment())) ||
      ((this.isCompleted() || this.isExpired()) && this.completedAt.isAfter(this.loadPlanningEnd))
    ) {
        return TripDelayType.LATE;
    }

    if (this.isNotTerminated() && this.latestEta() && this.loadPlanningEnd.isBefore(this.latestEta().clone().add(30, 'minutes'))) {
      return TripDelayType.LIKELY_LATE;
    }

    return TripDelayType.ON_TIME;
  }

  getTripState(dateService: DateService): { status: TripDelayType, msgDate: string, msgKey: string} {
    const tripStatus = this.getTripStatus();
    switch (tripStatus) {
      case TripDelayType.UNKNOWN:
        return {
          status: tripStatus,
          msgDate: null,
          msgKey: 'realtime-tracking.common.trip-status.no-dead-line-given'
        };

      case TripDelayType.LATE:
        return {
          status: TripDelayType.LATE,
          msgDate: this.isNotTerminated() ? dateService.durationTo(this.loadPlanningEnd, moment(), true).duration : dateService.durationTo(this.completedAt, this.loadPlanningEnd, true).duration,
          msgKey: 'realtime-tracking.common.trip-status.@late'
        };

      case TripDelayType.LIKELY_LATE:
      return {
        status: tripStatus,
        msgDate: dateService.durationTo(this.loadPlanningEnd, this.latestEta(), true).duration,
        msgKey: 'realtime-tracking.common.trip-status.@risk-to-be-late'
      };

      case TripDelayType.ON_TIME:
        return {
          status: tripStatus,
          msgDate: null,
          msgKey: 'realtime-tracking.common.trip-status.On-time'
        };
    }
  }

  getVehicles(): string {
    if (this.loadHandovers.length > 0) {
      const releasingVehicles = this.loadHandovers.map(loadHandover => ({vehicle: loadHandover.releasedByVehicle, at: loadHandover.releasedAt}));
      const takingVehicles = this.loadHandovers.map(loadHandover => ({vehicle: loadHandover.takenByVehicle, at: loadHandover.takenAt}));
      return [...releasingVehicles, ...takingVehicles]
        .filter(value => value.vehicle != null && value.at != null)
        .sort((a, b) => a.at.isBefore(b.at) ? -1 : 1)
        .map(value => value.vehicle)
        .filter((vehicle, index, self) => self.indexOf(vehicle) === index)
        .join(', ');
    } else {
      return this.vehicle ? this.vehicle : 'Unknown';
    }
  }

  getSalesOrderNumbers(): string[] {
    return Array.from(this.salesOrderNumbersByDeliveryNumbers.values());
  }
}
